// What the supply flow's view and panel share: how a node or band reads, and
// the selection of Explore that reproduces it where one filter does.

import {
  SupplyFlow,
  SupplyFlowBand,
  SupplyFlowBucket,
  SupplyFlowColumnId,
  SupplyFlowNode,
} from "../../../types/analytics";
import { AnalyticsSelection, selectionOf } from "../analyticsState";

export const counted = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

// The column a node id belongs to, from its prefix; the id is split on the
// first colon only, as class ids carry colons of their own
export const columnOfNodeId = (id: string): SupplyFlowColumnId | null => {
  const prefix = id.slice(0, id.indexOf(":"));
  switch (prefix) {
    case "host":
      return "hosts";
    case "kind":
      return "kinds";
    case "maker":
    case "makers":
      return "makers";
    case "country":
      return "countries";
    default:
      return null;
  }
};

export const COLUMN_NOUNS: Record<SupplyFlowColumnId, string> = {
  hosts: "Host kind",
  kinds: "Kind",
  makers: "Maker",
  countries: "Country",
};

export const MORE_MAKERS_ID = "makers:more";

export const isBucketNode = (node: SupplyFlowNode): boolean =>
  node.id === "host:other" ||
  node.id === "kind:other" ||
  node.id === MORE_MAKERS_ID ||
  node.id === "country:unknown";

// How a node reads: the "more makers" bucket says how many it holds
export const nodeLabel = (node: SupplyFlowNode): string =>
  node.id === MORE_MAKERS_ID ? `${node.omitted ?? node.product_count} more makers` : node.label;

export const BUCKET_WORDS: Record<SupplyFlowBucket, string> = {
  domestic: "home jurisdiction",
  trusted: "trusted",
  other: "other",
  unknown: "country unknown",
};

export const indexNodes = (flow: SupplyFlow): Map<string, SupplyFlowNode> =>
  new Map(flow.columns.flatMap((column) => column.nodes).map((node) => [node.id, node]));

// The selection that opens a node's products in Explore, where one filter
// reproduces them: a kind by its category, a maker by its maker pick. The
// pick replaces the scope's own picks of that filter. A host counts hosts,
// not products; a country or a bucket has no filter. Those are null.
export const nodeSelection = (
  selection: AnalyticsSelection,
  node: SupplyFlowNode,
): AnalyticsSelection | null => {
  const base = selectionOf(selection);
  const column = columnOfNodeId(node.id);
  if (!node.entity) {
    return null;
  }
  if (column === "kinds") {
    return { ...base, category: [node.entity.id] };
  }
  if (column === "makers") {
    return { ...base, maker: [node.entity.id] };
  }
  return null;
};

// The selection that opens a band's products: the kind end by its category,
// the maker end by its maker pick, both when the band joins them. A need
// band, which is about hosts that hold nothing of the kind, has none.
export const bandSelection = (
  selection: AnalyticsSelection,
  band: SupplyFlowBand,
  nodes: Map<string, SupplyFlowNode>,
): AnalyticsSelection | null => {
  if (band.need) {
    return null;
  }
  let picked: AnalyticsSelection | null = null;
  for (const id of [band.source, band.target]) {
    const node = nodes.get(id);
    const column = node ? columnOfNodeId(node.id) : null;
    if (node?.entity && (column === "kinds" || column === "makers")) {
      picked = {
        ...(picked ?? selectionOf(selection)),
        ...(column === "kinds"
          ? { category: [node.entity.id] }
          : { maker: [node.entity.id] }),
      };
    }
  }
  return picked;
};

// The words of a band: its two ends and what it is
export const bandTitle = (
  band: SupplyFlowBand,
  nodes: Map<string, SupplyFlowNode>,
): string => {
  const end = (id: string) => {
    const node = nodes.get(id);
    return node ? nodeLabel(node) : id;
  };
  return `${end(band.source)} to ${end(band.target)}`;
};

export const needWords = (count: number): string =>
  `${counted(count, "host")} ${count === 1 ? "states" : "state"} the need and ${count === 1 ? "holds" : "hold"} no part of this kind on record`;

// The bands of the response that touch a node
export const bandsOf = (flow: SupplyFlow, id: string): SupplyFlowBand[] =>
  flow.bands.filter((band) => band.source === id || band.target === id);
